import { create } from 'zustand';
import { en } from './locales/en';
import type { Dictionary, TranslationKey } from './locales/en';
import { fr } from './locales/fr';
import { es } from './locales/es';
import { de } from './locales/de';
import { it } from './locales/it';
import { pt } from './locales/pt';

export type { TranslationKey, Dictionary };

export const LOCALES = ['en', 'fr', 'es', 'de', 'it', 'pt'] as const;
export type Locale = (typeof LOCALES)[number];

export const DEFAULT_LOCALE: Locale = 'en';

/** Endonyms: a language picker should read in its own language. */
export const LOCALE_NAMES: Record<Locale, string> = {
  en: 'English',
  fr: 'Français',
  es: 'Español',
  de: 'Deutsch',
  it: 'Italiano',
  pt: 'Português',
};

/** BCP 47 tags used for Intl date, time and number formatting. */
export const LOCALE_TAGS: Record<Locale, string> = {
  en: 'en-GB',
  fr: 'fr-FR',
  es: 'es-ES',
  de: 'de-DE',
  it: 'it-IT',
  pt: 'pt-PT',
};

const DICTIONARIES: Record<Locale, Dictionary> = { en, fr, es, de, it, pt };

const COOKIE_NAME = 'once.lang';
const COOKIE_MAX_AGE = 60 * 60 * 24 * 365;

/* -------------------------------------------------------------------------- */
/* Cookie persistence                                                          */
/* -------------------------------------------------------------------------- */

function readCookie(): Locale | null {
  if (typeof document === 'undefined') return null;

  const match = document.cookie
    .split('; ')
    .find((entry) => entry.startsWith(`${COOKIE_NAME}=`));
  if (!match) return null;

  const value = decodeURIComponent(match.slice(COOKIE_NAME.length + 1));
  return isLocale(value) ? value : null;
}

function writeCookie(locale: Locale): void {
  if (typeof document === 'undefined') return;
  // Lax rather than Strict: a shared link opened from a messaging app is a
  // cross-site navigation, and the recipient should still get their language.
  document.cookie =
    `${COOKIE_NAME}=${encodeURIComponent(locale)}; path=/; max-age=${COOKIE_MAX_AGE}; SameSite=Lax` +
    (typeof location !== 'undefined' && location.protocol === 'https:' ? '; Secure' : '');
}

export function isLocale(value: string): value is Locale {
  return (LOCALES as readonly string[]).includes(value);
}

/* -------------------------------------------------------------------------- */
/* Detection                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * Resolves the locale, in order of authority:
 *   1. an explicit choice stored in the cookie
 *   2. the browser's preferred languages, matched on the base subtag so
 *      `pt-BR`, `de-AT` and `en-US` all resolve
 *   3. English
 */
export function detectLocale(): Locale {
  const stored = readCookie();
  if (stored) return stored;

  if (typeof navigator !== 'undefined') {
    const preferences = navigator.languages?.length
      ? navigator.languages
      : [navigator.language].filter(Boolean);

    for (const preference of preferences) {
      // `fr-CA` → `fr`. Region is irrelevant: we ship one variant per language.
      const base = preference.toLowerCase().split('-')[0];
      if (base && isLocale(base)) return base;
    }
  }

  return DEFAULT_LOCALE;
}

/* -------------------------------------------------------------------------- */
/* Translation                                                                 */
/* -------------------------------------------------------------------------- */

export type TranslateValues = Record<string, string | number>;

/**
 * Looks a key up and substitutes `{name}` placeholders.
 *
 * Falls back to English for a key the active locale somehow lacks - which the
 * type system already prevents at build time, but a stale cached bundle should
 * degrade to readable English rather than render a raw key.
 */
export function translate(
  locale: Locale,
  key: TranslationKey,
  values?: TranslateValues,
): string {
  const template = DICTIONARIES[locale][key] ?? en[key] ?? key;
  if (!values) return template;

  return template.replace(/\{(\w+)\}/g, (match, name: string) =>
    name in values ? String(values[name]) : match,
  );
}

/* -------------------------------------------------------------------------- */
/* Store                                                                       */
/* -------------------------------------------------------------------------- */

/** Keeps the document in step with the active locale. */
function applyToDocument(locale: Locale): void {
  if (typeof document === 'undefined') return;
  document.documentElement.lang = locale;
}

interface I18nState {
  locale: Locale;
  /** Bound translator; changes identity on every locale switch so memoised
   *  components re-render. */
  t: (key: TranslationKey, values?: TranslateValues) => string;
  setLocale: (locale: Locale) => void;
  init: () => void;
}

function makeTranslator(locale: Locale) {
  return (key: TranslationKey, values?: TranslateValues) =>
    translate(locale, key, values);
}

const initialLocale = detectLocale();

export const useI18n = create<I18nState>((set) => ({
  locale: initialLocale,
  t: makeTranslator(initialLocale),

  setLocale: (locale) => {
    writeCookie(locale);
    applyToDocument(locale);
    set({ locale, t: makeTranslator(locale) });
  },

  init: () => {
    const locale = detectLocale();
    applyToDocument(locale);
    // Only persist once the user chooses; a detected locale stays implicit so
    // a later browser change is still honoured.
    set({ locale, t: makeTranslator(locale) });
  },
}));

/** Convenience hook: `const t = useT()`. */
export function useT() {
  return useI18n((state) => state.t);
}

/** Active BCP 47 tag, for Intl formatting. */
export function useLocaleTag(): string {
  return useI18n((state) => LOCALE_TAGS[state.locale]);
}

/** Non-reactive read, for call sites outside React (stores, error mappers). */
export function currentLocale(): Locale {
  return useI18n.getState().locale;
}

export function tStatic(key: TranslationKey, values?: TranslateValues): string {
  return translate(currentLocale(), key, values);
}
